import { create } from 'zustand'
import type { NotificationRecord } from '../models/notification.model'
import { notificationsService } from '../services/notifications.service'
import { notificationsRepo } from '../services/notifications.repo'

interface NotificationsState {
  notifications: NotificationRecord[]
  hydrated: boolean

  refresh: () => void
  notify: (input: Parameters<typeof notificationsService.notify>[0]) => void
  /** Returns `true` if a new notification was inserted, `false` if the key
   *  was already present (so callers can also de-dupe side effects like
   *  playing a sound). */
  notifyReminderOnce: (
    reminderKey: string,
    input: Parameters<typeof notificationsService.notify>[0],
  ) => boolean
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  remove: (id: string) => void
  clearAll: () => void
}

const reload = (): NotificationRecord[] => notificationsRepo.all()

export const useNotificationsStore = create<NotificationsState>((set) => ({
  notifications: typeof window === 'undefined' ? [] : reload(),
  hydrated: typeof window !== 'undefined',

  refresh: () => set({ notifications: reload() }),

  notify: (input) => {
    notificationsService.notify(input)
    set({ notifications: reload() })
  },

  notifyReminderOnce: (reminderKey, input) => {
    const created = notificationsService.notifyReminderOnce(reminderKey, input)
    if (created) {
      set({ notifications: reload() })
      return true
    }
    return false
  },

  markAsRead: (id) => {
    notificationsService.markAsRead(id)
    set({ notifications: reload() })
  },

  markAllAsRead: () => {
    notificationsService.markAllAsRead()
    set({ notifications: reload() })
  },

  remove: (id) => {
    notificationsService.remove(id)
    set({ notifications: reload() })
  },

  clearAll: () => {
    notificationsService.clearAll()
    set({ notifications: reload() })
  },
}))

export const selectUnreadCount = (s: NotificationsState): number =>
  s.notifications.filter((n) => !n.isRead).length
